import IconButton from 'components/IconButton';
import TextField from 'components/TextField';
import React from 'react';
import {
  TSendMessage,
  TTextFieldChangeEvent,
} from 'types';
import UploadFile from '../UploadFile';
import {
  MAX_FILE_SIZE_PUBNUB,
  ONE_MB,
  PDF_MIME_TYPE,
  TEXT_STRING,
} from '../../../constants';
import {
  CHAT_ACCEPTED_FILE_TYPES,
  MAX_CHAT_PDF_SIZE,
  MAX_CHAT_VIDEO_SIZE,
  getChatFileKind,
  isAcceptedChatFileType,
} from '../constants';
import { FileView } from 'components';
import Icon from 'components/Icon';
import { useToast } from 'hooks';

type Props = {
  sendMessage: (payload: TSendMessage) => Promise<void>;
  onTyping: (status: boolean) => void;
  showTyping: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
};

let timeout: NodeJS.Timeout;

const TYPING_TIME = 3000;

const MessageInput = ({
  sendMessage,
  onTyping,
  showTyping,
  onBlur,
  onFocus,
}: Props) => {
  // Hooks
  const ref = React.useRef(true);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // const { checkNeedToLogin } = useAuth();
  const { toastError } = useToast();

  // States
  const [message, setMessage] = React.useState<string>('');
  const [files, setFiles] = React.useState<File[]>([]);
  const [isTyping, setIsTyping] = React.useState<boolean>(false);
  const [isSending, setIsSending] = React.useState<boolean>(false);
  const [buttonClicked, setButtonClicked] = React.useState<boolean>(false);

  // Callbacks
  const onChangeFiles = React.useCallback(
    (files: File[]) => {
      if (isSending) return;
      // accept 属性はダイアログの「すべてのファイル」で回避できるため、選択後にも検証する
      if (files[0] && !isAcceptedChatFileType(files[0].type)) {
        return toastError(new Error(TEXT_STRING.ERROR.UNSUPPORTED_FILE_TYPE));
      }
      const kind = getChatFileKind(files[0]?.type);
      if (kind === 'pdf' && files[0]?.size > MAX_CHAT_PDF_SIZE) {
        return toastError(
          new Error(
            TEXT_STRING.ERROR.PDF_LIMIT_SIZE.replace(
              '$size',
              `${MAX_CHAT_PDF_SIZE / ONE_MB}mb`
            )
          )
        );
      }
      if (kind === 'video' && files[0]?.size > MAX_CHAT_VIDEO_SIZE) {
        return toastError(
          new Error(
            TEXT_STRING.ERROR.VIDEO_LIMIT_SIZE.replace(
              '$size',
              `${MAX_CHAT_VIDEO_SIZE / ONE_MB}mb`
            )
          )
        );
      }
      if (kind === 'image' && files[0]?.size > MAX_FILE_SIZE_PUBNUB) {
        return toastError(
          new Error(
            TEXT_STRING.ERROR.LIMIT_SIZE.replace(
              '$size',
              `${MAX_FILE_SIZE_PUBNUB / ONE_MB}mb`
            )
          )
        );
      }
      setFiles(files);
    },
    [isSending, toastError]
  );

  const onChangeMessage = React.useCallback(
    (event: TTextFieldChangeEvent) => {
      if (isSending) return;
      setMessage(event.target.value);
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        setIsTyping(false);
      }, TYPING_TIME);
      setIsTyping(true);
    },
    [isSending, setMessage]
  );

  const onSendMessage = React.useCallback(() => {
    if (isSending) return;
    if (inputRef.current) inputRef.current.focus();
    if (message.trim() || files.length) {
      setIsSending(true);
      sendMessage({ message: message?.trim(), file: files?.[0] })
        .then(() => {
          onTyping(false);
          setMessage('');
          setFiles([]);
        })
        .catch((error) => {
          console.error('❌ [MESSAGE_INPUT] Error sending message:', error);
          toastError(error);
        })
        .finally(() => {
          setIsSending(false);
        });
    }
  }, [isSending, message, files, sendMessage, onTyping, toastError]);

  const handleBlur = React.useCallback(() => {
    if (buttonClicked) return setButtonClicked(false);
    onBlur && onBlur();
  }, [buttonClicked, onBlur]);


  // Effect
  React.useEffect(() => {
    if (!ref.current) {
      onTyping(isTyping);
    } else ref.current = false;
    return () => { };
  }, [isTyping, onTyping]);

  return (
    <>
      {!!files?.length && (
        <div className="flex gap-4 overflow-x-auto p-2 h-24">
          {files.map((file, index) =>
            // PDF は FileView（img/video 前提）では表示できないため、ファイル名チップで表示する
            file.type === PDF_MIME_TYPE ? (
              <div
                key={index}
                data-testid="pdf-file-chip"
                className="h-full relative flex-shrink-0 flex items-center gap-2 rounded-lg border border-neutral-200 px-3 pr-10"
              >
                <IconButton
                  icon="close"
                  onClick={() => {
                    if (isSending) return;
                    setFiles((prev) => prev.filter((_, idx) => idx !== index));
                  }}
                  className="absolute top-0 right-0 z-[1]"
                  disabled={isSending}
                  transparent
                />
                <span className="shrink-0">
                  <Icon icon="pdf" width={20} height={20} />
                </span>
                <span className="text-sm break-all line-clamp-2">
                  {file.name}
                </span>
              </div>
            ) : (
              <FileView
                className="h-full"
                key={index}
                file={file}
                onRemove={
                  isSending
                    ? undefined
                    : () => {
                        setFiles((prev) =>
                          prev.filter((_, idx) => idx !== index)
                        );
                      }
                }
              />
            )
          )}
        </div>
      )}
      {showTyping && (
        <div className="flex space-x-2 items-end px-2">
          <p className="">{TEXT_STRING.COMMON.TYPING}</p>
          <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.05s] mb-1" />
          <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.025s] mb-1" />
          <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce mb-1" />
        </div>
      )}

      <div className="flex gap-8 items-center pl-6 pr-5 py-6 rounded-lg relative">
        <UploadFile
          onChangeFiles={onChangeFiles}
          accept={CHAT_ACCEPTED_FILE_TYPES.join(',')}
          disabled={isSending}
          buttonClass="scale-[1.75]"
        />

        <TextField
          type="textarea"
          name="message"
          onChange={onChangeMessage}
          value={message}
          rows={Math.min(message.split('\n').length || 1, 5)}
          className="w-full"
          inputClass="resize-none py-4"
          placeholder={TEXT_STRING.MESSAGE.INPUT_PLACEHOLDER}
          readOnly={isSending}
          inputRef={inputRef}
          onBlur={handleBlur}
          onFocus={() => {
            onFocus && onFocus();
          }}
        />
        <div onMouseDown={() => setButtonClicked(true)}>
          <IconButton
            icon="sendMessage"
            onClick={() => onSendMessage()}
            className="text-blue-600 scale-[1.75]"
            transparent
            disabled={isSending}
          />
        </div>
      </div>
    </>
  );
};

export default MessageInput;
